import { z } from 'zod';

export const UserRole = z.enum(['SUPER_ADMIN', 'SHOP_OWNER', 'BARBER', 'CUSTOMER']);
export type UserRole = z.infer<typeof UserRole>;

export const AppointmentStatus = z.enum([
  'PENDING',
  'CONFIRMED',
  'CHECKED_IN',
  'IN_SERVICE',
  'COMPLETED',
  'CANCELED',
  'NO_SHOW',
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatus>;

export const SubscriptionStatus = z.enum([
  'TRIALING',
  'PENDING_PAYMENT',
  'ACTIVE',
  'PAST_DUE',
  'CANCELED',
  'EXPIRED',
]);

export const AdminShopStatus = z.enum([
  'TRIALING',
  'PENDING_PAYMENT',
  'ACTIVE',
  'PAST_DUE',
  'CANCELED',
  'EXPIRED',
  'NO_SUBSCRIPTION',
]);
export type AdminShopStatus = z.infer<typeof AdminShopStatus>;

export const adminShopsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(5).max(50).default(10),
  search: z.string().trim().max(100).default(''),
});
export type AdminShopsQuery = z.infer<typeof adminShopsQuerySchema>;

export const loginSchema = z.object({
  email: z.email('Informe um e-mail válido').transform((value) => value.toLowerCase()),
  password: z.string().min(8, 'A senha deve ter pelo menos 8 caracteres'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = loginSchema.extend({
  name: z.string().trim().min(2, 'Informe seu nome').max(100),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const shopSchema = z.object({
  name: z.string().trim().min(2).max(100),
  slug: z
    .string()
    .trim()
    .min(3)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use letras minúsculas, números e hífens'),
  phone: z.string().trim().min(10).max(20),
  address: z.string().trim().min(5).max(240),
  timezone: z.string().default('America/Sao_Paulo'),
});
export type ShopInput = z.infer<typeof shopSchema>;

export const serviceSchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(240).optional().default(''),
  durationMinutes: z.number().int().min(10).max(480),
  priceCents: z.number().int().nonnegative().max(10_000_000),
});
export type ServiceInput = z.infer<typeof serviceSchema>;

export const barberSchema = z.object({
  name: z.string().trim().min(2).max(100),
  phone: z.string().trim().min(10).max(20).optional(),
  email: z.email().optional(),
});
export type BarberInput = z.infer<typeof barberSchema>;

export const workScheduleSchema = z.object({
  barberId: z.uuid(),
  days: z
    .array(
      z.object({
        dayOfWeek: z.number().int().min(0).max(6),
        startMinute: z.number().int().min(0).max(1439),
        endMinute: z.number().int().min(1).max(1440),
        active: z.boolean(),
      }),
    )
    .refine((days) => days.every((day) => day.startMinute < day.endMinute), 'Horário inválido'),
});
export type WorkScheduleInput = z.infer<typeof workScheduleSchema>;

export const availabilityQuerySchema = z.object({
  serviceId: z.uuid(),
  barberId: z.union([z.uuid(), z.literal('any')]),
  date: z.iso.date(),
});
export type AvailabilityQuery = z.infer<typeof availabilityQuerySchema>;

export const createAppointmentSchema = z.object({
  serviceId: z.uuid(),
  barberId: z.uuid(),
  startsAt: z.iso.datetime({ offset: true }),
  customer: z.object({
    name: z.string().trim().min(2).max(100),
    phone: z.string().trim().min(10).max(20),
    email: z.email(),
  }),
  notes: z.string().trim().max(500).optional(),
  turnstileToken: z.string().optional(),
});
export type CreateAppointmentInput = z.infer<typeof createAppointmentSchema>;

export const agendaDayQuerySchema = z.object({
  date: z.iso.date(),
});
export type AgendaDayQuery = z.infer<typeof agendaDayQuerySchema>;

export const updateAppointmentStatusSchema = z.object({
  status: AppointmentStatus,
});
export type UpdateAppointmentStatusInput = z.infer<typeof updateAppointmentStatusSchema>;

export interface AgendaProfessional {
  id: string;
  name: string;
}

export interface AgendaAppointment {
  id: string;
  startsAt: string;
  endsAt: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  serviceId: string;
  serviceName: string;
  priceCents: number;
  barberId: string;
  barberName: string;
  status: AppointmentStatus;
  notes?: string;
}

export interface AgendaDayResponse {
  shop: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
    published: boolean;
  };
  professionals: AgendaProfessional[];
  appointments: AgendaAppointment[];
}

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  tenantId?: string;
  shopId?: string;
}

export interface AuthResponse {
  accessToken: string;
  user: SessionUser;
}

export interface ApiError {
  statusCode: number;
  code: string;
  message: string;
  requestId?: string;
}

export interface AdminPlatformMetrics {
  totalShops: number;
  activeTrials: number;
  activeSubscriptions: number;
  pastDueSubscriptions: number;
  monthlyRecurringRevenueCents: number;
  pendingPayments: number;
  activeCampaigns: number;
  newShopsLast30Days: number;
  canceledSubscriptionsLast30Days: number;
}

export interface AdminShopListItem {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  ownerName: string;
  ownerEmail: string;
  ownerStatus: string;
  status: AdminShopStatus;
  planName?: string;
  trialEndsAt?: string;
  published: boolean;
  professionals: number;
  appointments: number;
  createdAt: string;
}

export interface AdminOverviewResponse {
  metrics: AdminPlatformMetrics;
  recentShops: AdminShopListItem[];
  generatedAt: string;
}

export interface AdminShopsResponse {
  items: AdminShopListItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface PublicService {
  id: string;
  name: string;
  description: string;
  durationMinutes: number;
  priceCents: number;
}

export interface PublicBarber {
  id: string;
  name: string;
}

export interface PublicShop {
  id: string;
  name: string;
  slug: string;
  phone: string;
  address: string;
  timezone: string;
  services: PublicService[];
  barbers: PublicBarber[];
}

export interface AvailabilitySlot {
  startsAt: string;
  endsAt: string;
  barberId: string;
  barberName: string;
}
